import React, { useEffect } from "react";
import { useHistory } from "react-router";
import { AuthProvider, useAuth } from "react-oidc-context";
import { AUTH_GATEWAY_OIDC_ISSUER, AUTH_CLIENT_ID } from "../env";
import { User } from "oidc-client-ts";
import { useLocalStorage } from "usehooks-ts";
import { useVisibilityChange } from "@uidotdev/usehooks";

type CachedAuthContext = {
  user?: User | null;
};

const AUTH_CONTEXT_STORAGE_KEY = "_auth_context";

export const AuthBouncer: React.FC = ({ children }) => {
  const history = useHistory();
  const { isLoading, isAuthenticated, signinRedirect, user } = useAuth();

  const [_, setCachedAuthContext] = useLocalStorage<CachedAuthContext>(
    AUTH_CONTEXT_STORAGE_KEY,
    {},
  );

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      signinRedirect({
        state: {
          returnTo: history?.location,
        },
      });
    }
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      setCachedAuthContext((prev) => ({ ...prev, user }));
    }
  }, [isAuthenticated, user]);

  return isLoading || !isAuthenticated ? <></> : <>{children}</>;
};

export const getCachedAuthContext = (): CachedAuthContext => {
  try {
    const stored = window.localStorage.getItem(AUTH_CONTEXT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as CachedAuthContext;
    }
  } catch (err) {
    console.error("Failed to get cached auth context", err);
  }
  return {};
};

/**
 * This component automatically manages silent renew of tokens when the document is visible
 */
const AutoRenew = () => {
  const documentVisible = useVisibilityChange();
  const auth = useAuth();
  // disable auto renew when document is not visible
  useEffect(() => {
    if (!documentVisible) {
      auth.stopSilentRenew();
    } else {
      auth.startSilentRenew();
    }
  }, [documentVisible]);

  return null;
};

/**
 * Need to wrap the AuthWrapper with BrowserRouter
 * As such to have access to the session history of the application
 */
export const AuthWrapper: React.FC = ({ children }) => {
  const history = useHistory();

  /**
   * Take users back to the route they intended to access before authentication
   */
  const onRedirectCallback = (user: User | undefined) => {
    const returnTo = user?.state?.returnTo || window.location.pathname;
    history.push(returnTo);
  };

  return (
    <AuthProvider
      authority={AUTH_GATEWAY_OIDC_ISSUER}
      client_id={AUTH_CLIENT_ID}
      redirect_uri={window.location.origin}
      // resource="compono-api"
      scope="openid email profile read:all"
      onSigninCallback={onRedirectCallback}
      automaticSilentRenew={true}
    >
      <AuthBouncer>{children}</AuthBouncer>
      <AutoRenew />
    </AuthProvider>
  );
};
